import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    effect,
    EffectRef,
    HostListener,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { UploadResponseItem } from '../../../interfaces/file-upload.interface';
import { LoggedUser } from '../../../interfaces/user.interface';
import { AuthService } from '../../../services/auth.service';
import { FileStateService } from '../../../services/file-state.service';
import HistoricalChatService from '../../../services/historical-chat.service';
import { SignalService } from '../../../services/signal.service';
import { TaapService } from '../../../services/taap.service';
import { UploadService } from '../../../services/upload-progress.service';
import { ChatHistoryItem } from '../../interfaces/get-history-params.interface';
import LoaderService from '../../loading.service';

const SELECTED_CONVERSATION_STORAGE_KEY = 'aidaSelectedConversationId';
const SEARCH_DEBOUNCE_MS = 2000;

// Build a short snippet from backend conversation record
/**
 *
 * @param chat
 * @param q
 */
function buildSnippetFromChat(chat: any, q?: string): string | undefined {
    if (!chat) {
        return undefined;
    }
    const sanitize = (s: string) => {
        if (!s) {
            return '';
        }
        // remove citation tokens like [citation:...]
        let out = s.replace(/\[citation:[^\]]+\]/gi, '');
        // remove patterns like context:[citation:...]
        out = out.replace(/context\s*:\s*\[[^\]]+\]/gi, '');
        // strip simple html tags
        out = out.replace(/<[^>]+>/g, '');
        // collapse whitespace
        out = out.replace(/\s+/g, ' ').trim();
        return out;
    };

    const highlightMatch = (text: string, query?: string) => {
        if (!text) {
            return text;
        }
        const clean = sanitize(text);
        if (!query) {
            return clean.slice(0, 250) + (clean.length > 250 ? '...' : '');
        }
        const terms: string[] = [];
        const phraseRegex = /"([^"]+)"/g;
        let m;
        const cleanedQ = query.replace(phraseRegex, (m0, p) => {
            terms.push(p);
            return ' ';
        });
        cleanedQ
            .split(/\s+/)
            .map((t) => t.trim())
            .filter((t) => t.length > 0)
            .forEach((t) => terms.push(t));

        // find earliest match among terms
        let idx = -1;
        let matched = '';
        for (const term of terms) {
            const i = clean.toLowerCase().indexOf(term.toLowerCase());
            if (i >= 0 && (idx === -1 || i < idx)) {
                idx = i;
                matched = term;
            }
        }

        if (idx === -1) {
            return clean.slice(0, 250) + (clean.length > 250 ? '...' : '');
        }

        const start = Math.max(0, idx - 60);
        const end = Math.min(clean.length, idx + 60 + matched.length);
        const snippet =
            (start > 0 ? '... ' : '') +
            clean.slice(start, end) +
            (end < clean.length ? ' ...' : '');
        return snippet;
    };

    if (typeof chat.result === 'string' && chat.result.trim().length > 0) {
        return highlightMatch(chat.result, q);
    }
    if (Array.isArray(chat.history) && chat.history.length > 0) {
        const assistantMsg = chat.history.find((h: any) => h && h.who === 1 && h.what);
        const text = (assistantMsg && assistantMsg.what) || chat.history[0].what;
        if (text && typeof text === 'string') {
            return highlightMatch(text, q);
        }
    }
    if (typeof chat.query === 'string' && chat.query.trim().length > 0) {
        return highlightMatch(chat.query, q);
    }
    return undefined;
}

@Component({
    selector: 'polaris-historical-chats',
    templateUrl: './historical-chats.component.html',
    styleUrl: './historical-chats.component.scss',
    standalone: false,
})
export class HistoricalChatsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    recentChats: ChatHistoryItem[] = [];

    favoriteChats: ChatHistoryItem[] = [];

    // Full original lists (unfiltered) — used to restore after search clears
    private _allRecentChats: ChatHistoryItem[] = [];
    private _allFavoriteChats: ChatHistoryItem[] = [];

    // Derived lists used for display (after search filtering)
    displayedRecents: ChatHistoryItem[] = [];
    displayedFavorites: ChatHistoryItem[] = [];

    // Search query for filtering recent and favorite lists
    searchQuery = '';

    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    // Sidebar collapsed state
    sidebarCollapsed = false;

    // No explicit search mode — searches titles and responses by default

    favoriteLimit = 10;
    recentLimit = 10;

    // Controls whether the lists are expanded (accordion behavior)
    // Default to collapsed on initial load so sections load data but remain closed
    recentOpen = false;
    favoritesOpen = false;

    selectedChatId: string | null = null;
    openMenuChatId: string | null = null;
    menuPosition: { top: string; left: string } = { top: '0px', left: '0px' };

    renameModal = {
        visible: false,
        chatId: '',
        oldTitle: '',
        newTitle: '',
        action: 'Rename' as 'Rename' | 'Delete' | 'DeleteFiles' | 'DeleteFile',
    };

    deleteModal = {
        visible: false,
        chatId: '',
        oldTitle: '',
        newTitle: '',
        action: 'Delete' as 'Rename' | 'Delete' | 'DeleteFiles' | 'DeleteFile',
    };

    @Output() chatActionModalOpenChange = new EventEmitter<boolean>();

    private cleanupEffect: EffectRef;

    filesReferenceCount = 0;

    /**
     *
     * @param event
     */
    onRecentDetailsToggle(event: Event): void {
        // Ensure we find the <details> element in case the event target is a child
        let el = event.target as HTMLElement | null;
        while (el && el.tagName.toLowerCase() !== 'details') {
            el = el.parentElement;
        }
        const details = el as HTMLDetailsElement | null;
        this.recentOpen = !!details?.open;
        // If recent was just opened, close favorites to enforce mutual exclusivity
        if (this.recentOpen) {
            this.favoritesOpen = false;
        }
        this.cdRef.detectChanges();
        // Animate heights for smooth transition
        requestAnimationFrame(() => this.updateAccordionHeights());
    }

    /**
     * Measure accordion content and set inline max-height to create smooth height transition.
     */
    private updateAccordionHeights(): void {
        try {
            const detailsEls = Array.from(
                document.querySelectorAll(
                    '.sidebar-recent-chats__section details.accordion-details'
                )
            ) as HTMLDetailsElement[];
            for (const details of detailsEls) {
                const content = details.querySelector(
                    '.sidebar-recent-chats__section__scroll-container'
                ) as HTMLElement | null;
                if (!content) {
                    continue;
                }
                // ensure overflow hidden for clean transition
                content.style.overflow = 'hidden';
                if (details.open) {
                    // measure natural height and apply it (but cap to available sidebar height)
                    content.style.transition = content.style.transition || '';
                    content.style.maxHeight = '';
                    const naturalH = content.scrollHeight;
                    // try reading CSS variable set by component; fallback to 40vh of viewport
                    let maxAllowed = 0;
                    try {
                        const sidebar = document.querySelector(
                            '.sidebar-recent-chats'
                        ) as HTMLElement | null;
                        // defensive: ensure sidebar is a real DOM element attached to a document
                        if (
                            sidebar &&
                            typeof window !== 'undefined' &&
                            sidebar.ownerDocument &&
                            sidebar.ownerDocument.defaultView &&
                            typeof window.getComputedStyle === 'function'
                        ) {
                            const val = window
                                .getComputedStyle(sidebar)
                                .getPropertyValue('--aida-sidebar-available-height');
                            if (val) {
                                const parsed = parseInt(val.trim(), 10);
                                if (!isNaN(parsed)) {
                                    maxAllowed = parsed - 35;
                                } // subtract inner header/padding
                            }
                        }
                    } catch (e) {
                        /* noop */
                    }
                    if (!maxAllowed || maxAllowed <= 0) {
                        maxAllowed = Math.floor(window.innerHeight * 0.4) - 35; // match 40dvh fallback
                    }
                    const appliedH = Math.min(naturalH, maxAllowed);
                    content.style.maxHeight = `${appliedH}px`;
                    content.style.opacity = '1';
                    const onEnd = () => {
                        // leave layout flexible after opening and allow vertical scrolling
                        try {
                            content.style.maxHeight = '';
                            content.style.overflowY = 'auto';
                        } catch (e) {
                            /* noop */
                        }
                    };
                    content.addEventListener('transitionend', onEnd, { once: true });
                } else {
                    // collapse: ensure overflow hidden and animate from current height -> 0
                    const currentH = content.scrollHeight;
                    content.style.overflowY = 'hidden';
                    content.style.maxHeight = `${currentH}px`;
                    content.style.opacity = '1';
                    requestAnimationFrame(() => {
                        content.style.maxHeight = '0px';
                        content.style.opacity = '0';
                    });
                }
            }
        } catch (e) {
            // noop - measuring could fail in some environments
        }
    }

    /**
     * Compute available vertical space for the sidebar and set a CSS variable
     * on the sidebar element so styles can use it to compute max-heights.
     */
    private updateSidebarAvailableHeight(): void {
        try {
            const sidebar = document.querySelector('.sidebar-recent-chats') as HTMLElement | null;
            if (!sidebar) {
                return;
            }
            const rect = sidebar.getBoundingClientRect();
            // leave 48px for header/search area and some padding at bottom
            const bottomPadding = 48;
            const available = Math.max(
                120,
                Math.floor(window.innerHeight - rect.top - bottomPadding)
            );
            sidebar.style.setProperty('--aida-sidebar-available-height', `${available}px`);
        } catch (e) {
            // noop
        }
    }

    /**
     *
     * @param event
     */
    onFavoritesDetailsToggle(event: Event): void {
        // Ensure we find the <details> element in case the event target is a child
        let el = event.target as HTMLElement | null;
        while (el && el.tagName.toLowerCase() !== 'details') {
            el = el.parentElement;
        }
        const details = el as HTMLDetailsElement | null;
        this.favoritesOpen = !!details?.open;
        // If favorites was just opened, close recents to enforce mutual exclusivity
        if (this.favoritesOpen) {
            this.recentOpen = false;
        }
        this.cdRef.detectChanges();
        // Animate heights for smooth transition
        requestAnimationFrame(() => this.updateAccordionHeights());
    }

    /**
     *
     * @param cdRef
     * @param _authService
     * @param _historicalChatService
     * @param router
     * @param route
     * @param signalService
     * @param taapService
     * @param fileStateService
     * @param loaderService
     * @param _uploadService
     */
    constructor(
        private cdRef: ChangeDetectorRef,
        private _authService: AuthService,
        private _historicalChatService: HistoricalChatService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private signalService: SignalService,
        private readonly taapService: TaapService,
        private readonly fileStateService: FileStateService,
        public loaderService: LoaderService,
        private _uploadService: UploadService
    ) {
        this.cleanupEffect = effect(() => {
            const msg = this.signalService.queryHistory();
            if (msg) {
                this.addRecentChat(msg);
            }
        });

        this.loadUploadedFilesCount();
    }

    /**
     *
     */
    private updateLimits(): void {
        const maxFavorites = 10;
        const totalLimit = 20;

        const favLen = this.displayedFavorites?.length ?? this.favoriteChats.length;
        const recLen = this.displayedRecents?.length ?? this.recentChats.length;

        this.favoriteLimit = Math.min(favLen, maxFavorites);
        this.recentLimit = Math.min(recLen, totalLimit - this.favoriteLimit);
        // Ensure the recent displayed array is truncated to the recentLimit for consistent rendering
        if (this.displayedRecents && this.displayedRecents.length > this.recentLimit) {
            this.displayedRecents = this.displayedRecents.slice(0, this.recentLimit);
        }
    }

    /**
     * Toggle showing more items for favorites or recent lists.
     * @param section 'favorites' | 'recent'
     */
    toggleShowMore(section: 'favorites' | 'recent'): void {
        const maxFavorites = 20;
        const totalLimit = 20;

        if (section === 'favorites') {
            if (this.favoriteLimit <= 10) {
                this.favoriteLimit = Math.min(this.favoriteChats.length, maxFavorites);
            } else {
                this.favoriteLimit = 10;
            }
        } else {
            // recent
            if (this.recentLimit <= 10) {
                // fill remaining up to totalLimit, but do not exceed available recents
                const remaining = Math.max(0, totalLimit - this.favoriteLimit);
                this.recentLimit = Math.min(this.recentChats.length, remaining || totalLimit);
            } else {
                this.recentLimit = 10;
            }
        }
        this.cdRef.detectChanges();
    }

    /**
     *
     */
    private loadConversationsByUser(): void {
        const userData = this._authService.getUserData();

        if (!userData?.username || !userData?.firstName) {
            return;
        }

        this._historicalChatService
            .getConversationsByUser({ email: userData.username, name: userData.firstName })
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                this.recentChats = [];
                this.favoriteChats = [];

                if (res && Array.isArray(res.recent)) {
                    this.recentChats = res.recent
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat),
                        }));
                }

                if (res && Array.isArray(res.favorites)) {
                    this.favoriteChats = res.favorites
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat),
                        }));
                }

                // Save the full unfiltered lists for restoring after search clears
                this._allRecentChats = [...this.recentChats];
                this._allFavoriteChats = [...this.favoriteChats];

                // Initialize displayed lists (no search applied yet)
                this.displayedFavorites = [...this.favoriteChats];
                this.displayedRecents = [...this.recentChats];
                // Compute limits after displayed lists are set so initial rendering shows items
                this.updateLimits();
                this.restoreSelectedChatFromSession();
                this.cdRef.detectChanges();
            });
    }

    /**
     *
     */
    ngOnInit() {
        const saved = sessionStorage.getItem('aidaSidebarCollapsed');
        this.sidebarCollapsed = saved === 'true';
        // mirror collapsed state on parent container so layout can respond
        const container = document.querySelector('.aida__container');
        if (container) {
            if (this.sidebarCollapsed) {
                container.classList.add('aida-sidebar-collapsed');
            } else {
                container.classList.remove('aida-sidebar-collapsed');
            }
        }

        // ensure accordion heights are set correctly on init
        requestAnimationFrame(() => this.updateAccordionHeights());
        // compute dynamic sidebar available height and set CSS variable
        requestAnimationFrame(() => this.updateSidebarAvailableHeight());
        this.loadConversationsByUser();
    }

    /**
     *
     */
    @HostListener('window:resize')
    onWindowResize(): void {
        // Close any open chat options menu on resize to avoid layout overlap
        this.openMenuChatId = null;
        this.cdRef.detectChanges();
        // recompute available height on resize
        this.updateSidebarAvailableHeight();
        // also update accordions if needed
        requestAnimationFrame(() => this.updateAccordionHeights());
    }

    /**
     * Ensures the sidebar is in expanded (non-collapsed) state.
     * Called when the mobile/tablet hamburger opens the sidebar overlay.
     */
    expandSidebar(): void {
        if (!this.sidebarCollapsed) {
            return;
        }
        this.sidebarCollapsed = false;
        sessionStorage.setItem('aidaSidebarCollapsed', 'false');
        const container = document.querySelector('.aida__container');
        if (container) {
            container.classList.remove('aida-sidebar-collapsed');
        }
        this.openMenuChatId = null;
        this.cdRef.detectChanges();
    }

    /**
     *
     */
    toggleSidebarCollapse(): void {
        this.sidebarCollapsed = !this.sidebarCollapsed;
        sessionStorage.setItem('aidaSidebarCollapsed', this.sidebarCollapsed ? 'true' : 'false');
        // update parent layout so main content expands/shrinks accordingly
        const container = document.querySelector('.aida__container');
        if (container) {
            if (this.sidebarCollapsed) {
                container.classList.add('aida-sidebar-collapsed');
            } else {
                container.classList.remove('aida-sidebar-collapsed');
            }
        }
        // Close any open chat options menu when toggling the sidebar
        this.openMenuChatId = null;
        this.cdRef.detectChanges();
    }

    /**
     * Collapsed icon actions
     */
    onCollapsedNewChat(): void {
        // behave like New Chat button: clear current conversation and navigate to /aida
        this.fileStateService.clearFiles();
        this._uploadService.setFile(null);
        // Don’t clear the raw disabled flag while any upload is still running in the background;
        // `attachmentDisabled()` already protects the UI, but this keeps the underlying state accurate too.
        if (!this._uploadService.isUploading()) {
            this.loaderService.setAttachmentDisabledFlag(false);
        }
        this.loaderService.setSendButtonDisabledFlag(false);
        this.signalService.resetCurrentConversation();
        this.unSelectChat();

        // If not on chat or files route, reload the page to ensure main area resets.
        // Skip the reload while any upload (e.g. My Uploads) is still in flight, since
        // that would destroy all Angular state and silently kill the in-progress upload.
        if (
            !this.router.url.includes('/chat') &&
            !this.router.url.includes('/files') &&
            !this._uploadService.isUploading()
        ) {
            window.location.reload();
            return;
        }

        // otherwise navigate to main Aida page
        this.router.navigate(['/aida']);
    }

    /**
     *
     */
    onCollapsedSearch(): void {
        this.sidebarCollapsed = false;
        this.recentOpen = false;
        this.favoritesOpen = false;
        sessionStorage.setItem('aidaSidebarCollapsed', 'false');
        const container = document.querySelector('.aida__container');
        if (container) {
            container.classList.remove('aida-sidebar-collapsed');
        }
        this.cdRef.detectChanges();
        setTimeout(() => {
            const input = document.querySelector(
                '.sidebar-recent-chats__search input'
            ) as HTMLInputElement | null;
            if (input) {
                input.focus();
            }
        }, 150);
    }

    /**
     *
     */
    onCollapsedRecent(): void {
        this.sidebarCollapsed = false;
        sessionStorage.setItem('aidaSidebarCollapsed', 'false');
        const container = document.querySelector('.aida__container');
        if (container) {
            container.classList.remove('aida-sidebar-collapsed');
        }
        this.recentOpen = true;
        this.favoritesOpen = false;
        this.cdRef.detectChanges();
        setTimeout(() => {
            const el = document.getElementById('recent-list');
            if (el) {
                el.focus();
            }
        }, 150);
        requestAnimationFrame(() => this.updateAccordionHeights());
    }

    /**
     *
     */
    onCollapsedFavorites(): void {
        this.sidebarCollapsed = false;
        sessionStorage.setItem('aidaSidebarCollapsed', 'false');
        const container = document.querySelector('.aida__container');
        if (container) {
            container.classList.remove('aida-sidebar-collapsed');
        }
        this.favoritesOpen = true;
        this.recentOpen = false;
        this.cdRef.detectChanges();
        setTimeout(() => {
            const el = document.getElementById('favorites-list');
            if (el) {
                el.focus();
            }
        }, 150);
        requestAnimationFrame(() => this.updateAccordionHeights());
    }

    /**
     *
     */
    onCollapsedUpload(): void {
        // keep sidebar collapsed but navigate to uploads
        this.leaveCurrentChat();
        this.router.navigate(['aida/files'], { replaceUrl: true });
    }

    /**
     *
     * @param chat
     */
    selectChat(chat: any) {
        this.openMenuChatId = null;
        if (!chat || !chat.conversationId) {
            console.error('Attempted to select chat without conversationId', chat);
            return;
        }
        const currentRoute = this.router.url.split(/[?#]/)[0].replace(/\/+$/, '');
        if (this.selectedChatId === chat.conversationId && currentRoute.endsWith('/chat')) {
            return;
        }
        this.selectedChatId = chat.conversationId;
        sessionStorage.setItem(SELECTED_CONVERSATION_STORAGE_KEY, chat.conversationId);

        this.router.navigateByUrl('/_refresh', { skipLocationChange: true }).then(() => {
            try {
                this.router.navigate(['chat'], {
                    state: chat.conversationId ? { conversationId: chat.conversationId } : {},
                    relativeTo: this.route,
                });
            } catch (err) {
                console.error('Navigation to conversation failed', err, chat);
            }
        });
    }

    /**
     *
     */
    unSelectChat(): void {
        this.selectedChatId = null;
        sessionStorage.removeItem(SELECTED_CONVERSATION_STORAGE_KEY);
        this.cdRef.detectChanges();
    }

    /**
     *
     */
    leaveCurrentChat(): void {
        this.unSelectChat();
        this.fileStateService.clearFiles();
        this._uploadService.setFile(null);
        this.signalService.resetCurrentConversation();
        this.loaderService.setAttachmentDisabledFlag(false);
        this.loaderService.setSendButtonDisabledFlag(false);
    }

    /**
     *
     */
    private restoreSelectedChatFromSession(): void {
        if (!this.router.url.includes('/chat')) {
            return;
        }

        const storedConversationId = sessionStorage.getItem(SELECTED_CONVERSATION_STORAGE_KEY);
        if (!storedConversationId) {
            return;
        }

        this.selectedChatId = storedConversationId;
    }

    /**
     *
     */
    myUploads(): void {
        this.leaveCurrentChat();
        this.router.navigate(['aida/files'], { replaceUrl: true });
    }

    /**
     *
     * @param conversationId
     * @param event
     */
    toggleOptions(conversationId: string, event: MouseEvent): void {
        event.stopPropagation();

        const btn = event.currentTarget as HTMLElement;
        const rect = btn.getBoundingClientRect();
        const gap = 28;

        this.openMenuChatId = this.openMenuChatId === conversationId ? null : conversationId;

        this.cdRef.detectChanges();

        const menuEl = document.querySelector('.chat-options-menu') as HTMLElement | null;

        const estimatedMenuW = 180;
        const estimatedMenuH = 120;

        const menuW = menuEl ? menuEl.offsetWidth || estimatedMenuW : estimatedMenuW;
        const menuH = menuEl ? menuEl.offsetHeight || estimatedMenuH : estimatedMenuH;

        let left = rect.right + gap;
        let top = rect.top + rect.height / 2 - gap;

        if (left + menuW > window.innerWidth - 8) {
            const leftCandidate = rect.left - gap - menuW;
            if (leftCandidate >= 8) {
                left = leftCandidate;
            } else {
                left = Math.max(8, window.innerWidth - menuW - 8);
            }
        }

        if (top < 8) {
            top = 8;
        }
        if (top + menuH > window.innerHeight - 8) {
            top = Math.max(8, window.innerHeight - menuH - 8);
        }

        this.menuPosition = {
            left: `${Math.round(left)}px`,
            top: `${Math.round(top)}px`,
        };
    }

    /**
     *
     */
    @HostListener('window:scroll')
    onWindowScroll() {
        // Close any open chat options menu on scroll to avoid layout overlap
        this.openMenuChatId = null;
        this.cdRef.detectChanges();

        // If query empty -> reload full lists
        if (!this.searchQuery) {
            this.loadConversationsByUser();
            return;
        }

        const userData = this._authService.getUserData();
        if (!userData?.username) {
            return;
        }

        // Call backend to search both titles and responses (server now searches historyTitle too)
        this._historicalChatService
            .getConversationsByUser(
                { email: userData.username, name: userData.firstName },
                this.searchQuery
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                this.recentChats = [];
                this.favoriteChats = [];

                if (res && Array.isArray(res.recent)) {
                    this.recentChats = res.recent
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat, this.searchQuery),
                        }));
                }

                if (res && Array.isArray(res.favorites)) {
                    this.favoriteChats = res.favorites
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat, this.searchQuery),
                        }));
                }

                this.displayedFavorites = [...this.favoriteChats];
                this.displayedRecents = [...this.recentChats];
                this.updateLimits();
                this.cdRef.detectChanges();
            });
    }

    /**
     *
     * @param event
     */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        this.closeMenuOnOutsideClick(event);
    }

    /**
     * Close any open chat options menu when clicking outside.
     * Kept for unit tests and external callers.
     * @param event
     */
    closeMenuOnOutsideClick(event: Event): void {
        const target = event?.target as HTMLElement | null;
        // If click occurred inside a chat options menu, do nothing
        if (target && target.closest && target.closest('.chat-options-menu')) {
            return;
        }
        this.openMenuChatId = null;
        this.cdRef.detectChanges();
    }

    /**
     * Public resize handler used by tests; mirrors HostListener behaviour.
     */
    onResize(): void {
        // Close any open menus on resize to avoid layout overlap
        this.openMenuChatId = null;
        this.updateSidebarAvailableHeight();
        requestAnimationFrame(() => this.updateAccordionHeights());
        this.cdRef.detectChanges();
    }

    /**
     *
     * @param chat
     */
    deleteChat(chat: ChatHistoryItem) {
        if (!chat.conversationId) {
            return;
        }
        this.deleteModal = {
            visible: true,
            chatId: chat.conversationId,
            oldTitle: chat.title,
            newTitle: chat.title,
            action: 'Delete' as const,
        };
        this.openMenuChatId = null;
        this.emitChatActionModalState();
    }
    /**
     *
     * @param chat
     */
    markAsFavoriteChat(chat: ChatHistoryItem) {
        if (!chat.conversationId) {
            return;
        }
        this._historicalChatService
            .updateFavoriteChatById(chat.conversationId, true)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response.success) {
                    this.loadConversationsByUser();
                }
            });
    }

    /**
     *
     * @param chat
     */
    unMarkAsFavoriteChat(chat: ChatHistoryItem) {
        if (!chat.conversationId) {
            return;
        }
        this._historicalChatService
            .updateFavoriteChatById(chat.conversationId, false)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response.success) {
                    this.loadConversationsByUser();
                }
            });
    }

    /**
     *
     * @param chatItem
     */
    renameChat(chatItem: any): void {
        this.renameModal = {
            visible: true,
            chatId: chatItem.conversationId,
            oldTitle: chatItem.title,
            newTitle: chatItem.title,
            action: 'Rename' as const,
        };
        this.openMenuChatId = null;
        this.emitChatActionModalState();
    }

    /**
     *
     */
    closeRenameModal(): void {
        this.renameModal.visible = false;
        this.emitChatActionModalState();
    }

    /**
     *
     */
    confirmRename(): void {
        const { chatId, newTitle } = this.renameModal;
        const trimmedTitle = newTitle.trim();
        if (!chatId || !trimmedTitle) {
            this.renameModal.visible = false;
            return;
        }

        if (trimmedTitle) {
            this._historicalChatService
                .renameConversationById(chatId, trimmedTitle)
                .pipe(takeUntil(this.destroy$))
                .subscribe((response) => {
                    if (response.success) {
                        this.loadConversationsByUser();
                    }
                });
        }

        this.renameModal.visible = false;
        this.emitChatActionModalState();
    }

    /**
     *
     */
    closeDeleteModal(): void {
        this.deleteModal.visible = false;
        this.emitChatActionModalState();
    }

    /**
     *
     */
    confirmDelete(): void {
        const { chatId } = this.deleteModal;
        this._historicalChatService
            .deleteChatById(chatId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                if (response.success) {
                    this.loadConversationsByUser();
                }
            });
        this.deleteModal.visible = false;
        this.emitChatActionModalState();
        this.leaveCurrentChat();
        this.router.navigate(['aida'], { replaceUrl: true });
    }

    /**
     * Notify parent container when rename/delete modals are open so mobile shell can react.
     */
    private emitChatActionModalState(): void {
        this.chatActionModalOpenChange.emit(this.renameModal.visible || this.deleteModal.visible);
    }

    /**
     *
     * @param chat
     */
    addRecentChat(chat: ChatHistoryItem) {
        this.recentChats.unshift(chat);
        // if the recent chats exceed 20, remove the last one
        if (this.recentChats.length > 20) {
            this.recentChats.pop();
        }

        // Keep the unfiltered "all recents" cache in sync too, otherwise clearing an
        // active search restores a stale list that is missing chats created while
        // the search was active (until the next search API call refreshes it).
        this._allRecentChats = this._allRecentChats.filter(
            (c) => c.conversationId !== chat.conversationId
        );
        this._allRecentChats.unshift(chat);
        if (this._allRecentChats.length > 20) {
            this._allRecentChats.pop();
        }

        // update displayed list according to search
        if (!this.searchQuery) {
            this.displayedRecents = [...this.recentChats];
        } else {
            const q = this.searchQuery.toLowerCase();
            this.displayedRecents = this.recentChats.filter((c) =>
                c.title.toLowerCase().includes(q)
            );
        }
        this.updateLimits();
    }

    /**
     * Filter favorites and recents based on `searchQuery` (case-insensitive)
     * @param query
     */
    onSearch(query: string): void {
        this.searchQuery = (query || '').trim();
        this.clearSearchDebounce();

        const userData = this._authService.getUserData();
        if (!userData?.username) {
            return;
        }

        // Empty query -> reset to full lists
        if (!this.searchQuery) {
            this.recentChats = [...this._allRecentChats];
            this.favoriteChats = [...this._allFavoriteChats];
            this.displayedFavorites = [...this._allFavoriteChats];
            this.displayedRecents = [...this._allRecentChats];
            this.updateLimits();
            this.cdRef.detectChanges();
            return;
        }

        const searchTerm = this.searchQuery;
        this.searchDebounceTimer = setTimeout(() => {
            this.searchConversations(userData, searchTerm);
        }, SEARCH_DEBOUNCE_MS);
    }

    /**
     * Clear the search input and reset displayed lists.
     */
    clearSearch(): void {
        this.onSearch('');
        const input = document.getElementById('searchChats') as HTMLInputElement | null;
        if (input) {
            input.value = '';
        }
    }

    /**
     * @param userData User profile used to scope conversation search.
     * @param searchTerm Search term that survived the debounce delay.
     */
    private searchConversations(userData: LoggedUser, searchTerm: string): void {
        this._historicalChatService
            .getConversationsByUser(
                { email: userData.username, name: userData.firstName },
                searchTerm
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                if (searchTerm !== this.searchQuery) {
                    return;
                }

                this.recentChats = [];
                this.favoriteChats = [];

                if (res && Array.isArray(res.recent)) {
                    this.recentChats = res.recent
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat, searchTerm),
                        }));
                }

                if (res && Array.isArray(res.favorites)) {
                    this.favoriteChats = res.favorites
                        .filter((chat) => chat._id && chat.historyTitle)
                        .map((chat) => ({
                            conversationId: String(chat._id),
                            title: chat.historyTitle,
                            createdAt: chat.createdAt ?? new Date(),
                            updatedAt: chat.updatedAt ?? new Date(),
                            snippet:
                                (chat as any).matchHits && (chat as any).matchHits.length > 0
                                    ? (chat as any).matchHits[0].excerpt
                                    : buildSnippetFromChat(chat, searchTerm),
                        }));
                }

                this.displayedFavorites = [...this.favoriteChats];
                this.displayedRecents = [...this.recentChats];
                this.updateLimits();
                this.cdRef.detectChanges();
            });
    }

    /**
     *
     */
    private clearSearchDebounce(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        }
    }

    /**
     *
     */
    loadUploadedFilesCount(): void {
        this.taapService.getFilesUpload().subscribe({
            next: (response: any) => {
                const data: UploadResponseItem[] = response?.body ?? [];
                this.fileStateService.setMyUploads(data.length);
                this.filesReferenceCount = data.length;
            },
            error: (err) => {
                console.error('Failed to load uploaded files', err);
            },
        });

        this.getFilesCount();
    }

    /**
     *
     */
    getFilesCount() {
        this.cleanupEffect = effect(() => {
            this.filesReferenceCount = this.fileStateService.getMyUploads();
        });
    }

    /**
     *
     */
    ngOnDestroy(): void {
        this.clearSearchDebounce();
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanupEffect.destroy();
    }
}
